import Preload from 'preload-it'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import CustomEase from 'gsap/CustomEase'

import { URL } from '../index'

gsap.registerPlugin(ScrollTrigger, CustomEase)

export default class Load {
    constructor(options){
        this.container = options.domElement
        this.scroll = options.scroll

        window.scrollTo(0, 0)
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0

        this.preload = Preload()
        
        this.progressBar = document.querySelector('.preload__progress-bar')
        this.preloadContent = document.querySelector('.preload__content')
        this.preloadCurtain = document.querySelector('.preload__curtain')

        this.randomY = gsap.utils.random(30, 60)
        this.randomDuration = gsap.utils.random(0.5, 1.5)
        this.randomStagger = gsap.utils.random(0.1, 0.4)

        this.setInitialHeroState()
        this.load()
    }

    setInitialHeroState() {
        if (document.querySelector('.hero')) {
            gsap.set('.hero__image', { scale: 1.2 })
            gsap.set('.hero__content__right > div', { autoAlpha: 0 })
            gsap.set('.hero__content__left .hidden-desktop', { autoAlpha: 0 })
            gsap.set('.hero__content__left .title', { autoAlpha: 0 })
            gsap.set('.hero__content__left__actions .button', { autoAlpha: 0 })
            gsap.set('.hero__content__left__navigation.hidden-desktop a', { autoAlpha: 0 })
            gsap.set('.hero__content__right__navigation a', { autoAlpha: 0 })
        }
    }

    load() {
        this.preload.fetch([
            `${URL}/public/images/favicon.png`,
            `${URL}/public/images/hero.jpg`,
        ]).then(() => {
            const enterAnimation = gsap.timeline({ delay: 0.3 })
            
            enterAnimation.to(this.preloadContent, {
                duration: 0.6,
                autoAlpha: 0,
                ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
            })
            
            enterAnimation.set(this.preloadCurtain, {
                height: '100%'
            })
            
            enterAnimation.to(this.preloadCurtain, {
                height: '0vh',
                duration: 0.8,
                ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000"),
                onComplete: () => {
                    if (this.scroll) {
                        this.scroll.start()
                    }
                }
            })

            const isHomePage = document.querySelector('.hero')
            const isEventPage = document.querySelector('#event .event__content')

            if (isHomePage) {
                const heroTimeline = gsap.timeline({ delay: 1.4 })

                heroTimeline.to('.hero__image',
                    {
                        scale: 1,
                        duration: 1.5,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    },
                    "-=0.5"
                )

                heroTimeline.fromTo('header',
                    {
                        autoAlpha: 0,
                        y: 30
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.8,
                        stagger: 0.12,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    },
                    "-=0.8"
                )
                
                heroTimeline.fromTo('.hero__content__left .hidden-desktop',
                    {
                        autoAlpha: 0,
                        y: -30
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 1,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    },
                    "-=0.6"
                )
                
                heroTimeline.fromTo('.hero__content__left .title',
                    {
                        autoAlpha: 0,
                        y: 40
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 1.2,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    },
                    "-=0.6"
                )
                
                heroTimeline.fromTo('.hero__content__left__actions .button',
                    {
                        autoAlpha: 0,
                        y: 30
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.9,
                        stagger: 0.15,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    },
                    "-=0.8"
                )
                
                heroTimeline.fromTo('.hero__content__left__navigation.hidden-desktop a',
                    {
                        autoAlpha: 0,
                        y: 20
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.8,
                        stagger: 0.1,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    },
                    "-=0.6"
                )

                heroTimeline.fromTo('.hero__content__right > div',
                    {
                        autoAlpha: 0,
                        y: -30
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 1,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    },
                    "-=0.7"
                )
                
                heroTimeline.fromTo('.hero__content__right__navigation a',
                    {
                        autoAlpha: 0,
                        x: 30
                    },
                    {
                        autoAlpha: 1,
                        x: 0,
                        duration: 0.8,
                        stagger: 0.12,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    },
                    "-=0.7"
                )
            } else if (isEventPage) {
                gsap.fromTo('header',
                    {
                        autoAlpha: 0,
                        y: 30
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.8,
                        delay: 0.8,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    }
                )

                gsap.fromTo('.event__hero-image img',
                    {
                        scale: 1.5,
                    },
                    {
                        scale: 1,
                        delay: 1,
                        duration: 2,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    }
                )
                
                gsap.fromTo('.event__hero-image',
                    {
                        clipPath: 'inset(100% 0 0 0)',
                    },
                    {
                        clipPath: 'inset(0% 0 0 0)',
                        delay: 1,
                        duration: .8,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
                    }
                )

                gsap.fromTo('.event__hero-content span, .event__hero-content .title',
                    {
                        autoAlpha: 0,
                        y: 30
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        delay: 1,
                        duration: .8,
                        stagger: 0.15,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000"),
                        onComplete: () => {
                            this.initEventBodyAnimations()
                        }
                    }
                )
            }
            
            document.querySelectorAll("footer").forEach((text) => {
                const tl = gsap.timeline({
                  scrollTrigger: {
                    trigger: text,
                    end: "+=60%",
                  },
                });
            
                tl.set(text, {
                  autoAlpha: 0,
                  y: this.randomY,
                }).fromTo(
                  text,
                  {
                    autoAlpha: 0,
                  },
                  {
                    autoAlpha: 1,
                    duration: 1.8,
                    y: 0,
                    duration: this.randomDuration,
                    ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000"),
                  }
                );
            });
        })
        
        this.preload.onprogress = event => {
            const progress = event.progress
            
            if (this.progressBar) {
                this.progressBar.style.width = `${progress}%`
            }
        }
    }

    initEventBodyAnimations() {
        const eventBody = document.querySelector('.event__body')
        if (!eventBody) return

        const elements = eventBody.querySelectorAll('p, h1, h2, h3, h4, h5, h6, ul, ol, img, figure, a')
        
        elements.forEach(element => {
            gsap.fromTo(element,
                {
                    autoAlpha: 0,
                    y: 40
                },
                {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.8,
                    ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000"),
                    scrollTrigger: {
                        trigger: element,
                        start: 'clamp(top 85%)',
                        once: true
                    }
                }
            )
        })

        const moreEventsSection = document.querySelector('.more-events')
        if (moreEventsSection) {
            const heading = moreEventsSection.querySelector('h3')
            if (heading) {
                gsap.fromTo(heading,
                    {
                        autoAlpha: 0,
                        y: 30
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.8,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000"),
                        scrollTrigger: {
                            trigger: heading,
                            start: 'clamp(top 85%)',
                            once: true
                        }
                    }
                )
            }

            const eventItems = moreEventsSection.querySelectorAll('.events__item')
            eventItems.forEach((item, index) => {
                gsap.fromTo(item,
                    {
                        autoAlpha: 0,
                        y: 40
                    },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.8,
                        delay: index * 0.1,
                        ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000"),
                        scrollTrigger: {
                            trigger: item,
                            start: 'clamp(top 85%)',
                            once: true
                        }
                    }
                )
            })
        }
    }
}